/**
 * Shared UI component for UI Address Form.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map, startWith } from 'rxjs';

import { AddressDetails } from '../../models/address.types';
import { AddressLookupOption, AddressLookupService } from '../../../features/agencies/address-lookup.service';
import { UiAutocompleteFieldComponent } from '../ui-autocomplete-field/ui-autocomplete-field.component';
import { UiFormFieldComponent } from '../ui-form-field/ui-form-field.component';

@Component({
  selector: 'ui-address-form',
  standalone: true,
  imports: [UiAutocompleteFieldComponent, UiFormFieldComponent, TranslatePipe],
  templateUrl: './ui-address-form.component.html',
  styleUrl: './ui-address-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiAddressFormComponent {
  private readonly addressLookupService = inject(AddressLookupService);
  private readonly translateService = inject(TranslateService);
  private readonly activeLanguage = toSignal(
    this.translateService.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translateService.currentLang || this.translateService.getDefaultLang() || 'en')
    ),
    { initialValue: this.translateService.currentLang || this.translateService.getDefaultLang() || 'en' }
  );
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  readonly value = input.required<AddressDetails>();
  readonly required = input(false);
  readonly showGeoFields = input(false);
  readonly searchLabel = input('');
  readonly searchHint = input('');
  readonly error = input('');
  readonly emptyMessage = input('');
  readonly fieldErrors = input<Record<string, string>>({});
  readonly valueChange = output<AddressDetails>();
  readonly lookupError = signal('');
  readonly isSearching = signal(false);
  readonly options = signal<AddressLookupOption[]>([]);
  private addressCache = new Map<string, AddressDetails>();
  readonly autocompleteOptions = computed(() =>
    this.options().map((option) => ({
      label: option.label,
      value: option.value,
      caption: option.caption
    }))
  );
  readonly resolvedError = computed(() => this.error() || this.lookupError());
  readonly resolvedSearchLabel = computed(() => this.searchLabel() || this.t('shared.addressForm.searchLabel'));
  readonly resolvedSearchHint = computed(() => this.searchHint() || this.t('shared.addressForm.searchHint'));
  readonly resolvedEmptyMessage = computed(() => this.emptyMessage() || this.t('shared.addressForm.emptyMessage'));

  onQueryChange(rawValue: string): void {
    const nextValue = rawValue.trim();
    const clearedAddressFields: Partial<AddressDetails> = nextValue
      ? {}
      : {
          streetAddress: '',
          locality: '',
          subLocality: '',
          region: '',
          country: '',
          postalCode: '',
          area: ''
        };

    this.lookupError.set('');
    this.updateValue({ formattedAddress: rawValue, ...clearedAddressFields }, [0, 0]);

    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    if (nextValue.length < 3) {
      this.options.set([]);
      this.isSearching.set(false);
      return;
    }

    this.isSearching.set(true);
    this.searchTimer = setTimeout(() => {
      this.fetchAddressOptions(rawValue).catch(() => {
        this.isSearching.set(false);
        this.options.set([]);
        this.lookupError.set(this.t('shared.addressForm.errors.lookupUnavailable'));
      });
    }, 300);
  }

  async selectAddress(query: string): Promise<void> {
    this.lookupError.set('');

    const cached = this.addressCache.get(query);
    if (cached) {
      this.valueChange.emit(cached);
      this.options.set([]);
      this.addressCache.clear();
      return;
    }

    this.isSearching.set(true);
    try {
      const address = await this.addressLookupService.getStructuredAddress(query);

      if (!address) {
        this.lookupError.set(this.t('shared.addressForm.errors.noAddressDetails'));
        return;
      }

      this.valueChange.emit(address);
      this.options.set([]);
    } catch {
      this.lookupError.set(this.t('shared.addressForm.errors.lookupUnavailable'));
    } finally {
      this.isSearching.set(false);
    }
  }

  setValue(key: keyof AddressDetails, rawValue: string): void {
    if (key === 'geo') {
      return;
    }

    this.updateValue({ [key]: rawValue } as Partial<AddressDetails>);
  }

  setGeo(index: 0 | 1, rawValue: string): void {
    const parsed = Number(rawValue);
    const current = this.value().geo;
    const geo: [number, number] = [current[0], current[1]];
    geo[index] = Number.isFinite(parsed) ? parsed : 0;
    this.updateValue({}, geo);
  }

  private updateValue(partial: Partial<AddressDetails>, nextGeo?: [number, number]): void {
    this.valueChange.emit({
      ...this.value(),
      ...partial,
      geo: nextGeo ?? this.value().geo
    });
  }

  private async fetchAddressOptions(query: string): Promise<void> {
    const options = await this.addressLookupService.search(query);
    this.addressCache.clear();
    for (const option of options) {
      if (option.structured) {
        this.addressCache.set(option.value, option.structured);
      }
    }
    this.options.set(options);
    this.isSearching.set(false);
  }

  private t(key: string): string {
    this.activeLanguage();
    return this.translateService.instant(key);
  }
}
